import BookingCard from '@/components/elements/Card/BookingCard/BookingCard';
import { LinkButton } from '@/components/elements/forms/buttons';
import Alert from '@/components/elements/notifications/Alert/Alert';
import Snackbar from '@/components/elements/notifications/Snackbar/Snackbar';
import Icon from '@/components/icons/Icon';
import EmptyState from '@/components/templates/EmptyState';
import { config } from '@/config';
import {
  canPayCardOnFileAfterBooking,
  canPayKlarnaAfterBooking,
  canPayQliroAfterBooking,
  getDynamicPriceListIdKey,
  getPlaceImage,
  getPlaceTimezone,
  getSalonDate,
  getTotalPrice,
  isBookingInThePast,
  isTotalPayable,
  showPrices,
  getImagePreview,
} from '@/helpers';
import { useAppSelector } from '@/hooks';
import { _s } from '@/locale';
import { ConfirmedBooking } from '@/types/api/services/booking';
import * as moment from 'moment';
import { Fragment } from 'react';
import { toast } from 'react-toastify';

const isToday = (momentDate) => momentDate.isSame(moment().startOf('day'), 'd');

const PRE_PAYMENT_OPTION = {
  COF: 'cof',
  KLARNA: 'klarna',
  QLIRO: 'qliro',
} as const;

type PrePayOption = (typeof PRE_PAYMENT_OPTION)[keyof typeof PRE_PAYMENT_OPTION];

export const getAvailablePrepayOption = (appointment: ConfirmedBooking): PrePayOption | undefined => {
  const anyEmployee = appointment?.extra?.anyEmployee;
  const priceListId = (!anyEmployee && appointment.services?.[0]?.employee?.about?.priceListId) || 0;
  const totalWithoutFee = getTotalPrice(
    appointment.services,
    appointment.place,
    priceListId,
    appointment.extra.giftcards || {},
    0,
    1,
    false,
    appointment.extra.campaigns || [],
    false,
    getDynamicPriceListIdKey(appointment),
    appointment.extra.appliedBundle,
  );
  const shouldShowPrices = showPrices(appointment.place, appointment.services);
  const isPayable = isTotalPayable(totalWithoutFee, shouldShowPrices);
  const isPayAtPlace = appointment.extra.paymentMethod === 0;

  const canPayWithKlarna = isPayable && canPayKlarnaAfterBooking(appointment);
  const canPayWithQliro = isPayable && canPayQliroAfterBooking(appointment);
  const canPayWithCardOnFile = isPayable && canPayCardOnFileAfterBooking(appointment);

  if (canPayWithCardOnFile && isPayAtPlace) {
    return 'cof';
  }

  if (canPayWithKlarna && isPayAtPlace) {
    return 'klarna';
  }

  if (canPayWithQliro && isPayAtPlace) {
    return 'qliro';
  }

  return undefined;
};

const handlePaymentClick = (id: number) => {};

export const PrePayAction = ({ appointmentId }: { appointmentId: number }) => {
  return (
    <LinkButton
      variant="primary"
      size="md"
      block
      onClick={() => handlePaymentClick(appointmentId)}
      to={`/booking/checkout/${appointmentId}`}
      leftIcon={<Icon variant="pre-pay" />}
      title={_s('payOnline')}>
      {_s('payOnline')}
    </LinkButton>
  );
};

export const MeetingLinkAction = ({ meetingLink }: { meetingLink: string }) => {
  const fullMeetingLink = meetingLink.startsWith('https://') ? meetingLink : `https://${meetingLink}`;

  const handleCopyLink = (fullMeetingLink: string) => {
    navigator.clipboard.writeText(fullMeetingLink);

    toast(({ closeToast }) => <Snackbar type="success" label={_s(`videoMeetingLinkCopy`)} onClose={closeToast} />);
  };

  return (
    <>
      <LinkButton
        variant="primary"
        size="md"
        block
        target="_blank"
        to={fullMeetingLink}
        leftIcon={<Icon variant="video-call" color="fg_primary_inverse" />}
        title={_s('videoMeeting')}>
        {_s('videoMeeting')}
      </LinkButton>
      <p className="py-xs flex justify-center gap-2">
        <span title={fullMeetingLink} className="text-fg_secondary truncate">
          {meetingLink}
        </span>
        <Icon variant="copy" className="cursor-pointer" onClick={() => handleCopyLink(fullMeetingLink)} />
      </p>
    </>
  );
};

const Appointment = ({ appointment, past }: { appointment: any; past: boolean }) => {
  const timezone = getPlaceTimezone(appointment.place);
  const dateTz = getSalonDate(appointment.start, timezone);
  const day = {
    weekDay: '',
    day: '',
    year: '',
    month: '',
    time: '',
  };
  day.weekDay = isToday(dateTz) ? _s('today') : _s(config.weekDaysShort[dateTz.day()]);
  day.day = dateTz.date().toString();
  day.year = dateTz.year().toString();
  day.month = config.monthsShort[dateTz.month()];
  day.time = dateTz.format('HH:mm');

  const date = `${day.weekDay} ${day.day} ${day.month} ${day.year} - ${day.time}`;

  let chosenServices =
    appointment.services[0].service.name +
    (appointment.services.length > 1 ? ' (+ ' + (appointment.services.length - 1) + ` ${_s('moreAmount')})` : '');

  const anyEmployee = appointment?.extra?.anyEmployee;
  const chosenEmployee = anyEmployee
    ? null
    : appointment.services.length > 0 && appointment.services[0]?.employee?.id
      ? appointment.services[0].employee?.about?.name
      : null;

  const placeName = appointment.place && appointment.place?.about?.name;

  let cardImage = getImagePreview(getPlaceImage(appointment.place), 'small', appointment.place.about.slug);
  const image = cardImage
    ? {
        src: cardImage,
        alt: appointment?.place?.about?.name,
      }
    : null;

  const availablePrePayOption = getAvailablePrepayOption(appointment);
  const isUpcoming = !isBookingInThePast(appointment);
  const status = appointment.status + (!isUpcoming ? 100 : 0);

  const meetingLink = appointment?.extra?.meetingLink;
  const hasMeetingLink = meetingLink && meetingLink.length > 0;

  const bottomSlot =
    hasMeetingLink || availablePrePayOption ? (
      <div className="flex flex-col gap-2">
        {availablePrePayOption && <PrePayAction appointmentId={appointment.id} />}
        {hasMeetingLink && <MeetingLinkAction meetingLink={meetingLink} />}
      </div>
    ) : undefined;

  return (
    <BookingCard
      src={{
        pathname: `/bokning/${appointment.id}`,
        state: { from: past ? '/bokningar/tidigare' : '/bokningar' },
      }}
      placeName={placeName}
      dateTime={date}
      image={image}
      performer={chosenEmployee}
      services={chosenServices}
      status={status}
      {...(bottomSlot && { bottomSlot })}
    />
  );
};

const AppointmentsList = ({ past, appointments }) => {
  const message = past ? 'pastAppointmentsMessage' : 'upcomingAppointmentsMessage';
  const label = past ? 'nopast' : 'noupcoming';
  const title = _s(label);
  const isWhitelabel = useAppSelector((state) => state?.settings?.isWhitelabel);

  const hasPrePayOption = ((appointments || []) as unknown[]).some((appointment) =>
    Boolean(getAvailablePrepayOption(appointment)),
  );

  return (
    <div id={past ? 'Past' : 'Upcoming'} role="tabpanel" aria-labelledby={past ? 'Past-tab' : 'Upcoming-tab'}>
      {!past && hasPrePayOption && (
        <div className="mb-lg">
          <Alert
            variant="information"
            leftSlot={<Icon variant="info-circle" color="information-900" />}
            body={_s('encouragePrePayBooking')}
          />
        </div>
      )}
      {appointments.length ? (
        <Fragment>
          {appointments.map((appointment) => (
            <div key={appointment.id} className="mb-md">
              <Appointment appointment={appointment} past={!!past} />
            </div>
          ))}
        </Fragment>
      ) : (
        <EmptyState
          body={_s(message)}
          title={title}
          icon={
            label === 'noupcoming' ? (
              <img src="/images/no-bookings1.svg" alt="" />
            ) : (
              <img src="/images/no-bookings2.svg" alt="" />
            )
          }
          cta={
            !isWhitelabel && (
              <LinkButton variant="primary" size="sm" to="/" title={_s('bookAService')}>
                {_s('bookAService')}
              </LinkButton>
            )
          }
        />
      )}
    </div>
  );
};

export default AppointmentsList;
